// How many people agreed to each consent, against how many were asked.
//
// THE NUMBER A CLIENT ASKS FOR FIRST, AND THE ONE MOST EASILY DRAWN AS A LIE.
// xp/operator's optIn.ts is written so that "a reader cannot come away believing
// higher is better", and every mechanism it uses to do that is one a screen can
// undo in a line:
//
//   A RATE NEAR EVERYBODY IS AN ALARM, NOT A RESULT. 97% is the signature of
//   people ticking whatever is in front of them to reach the button, which is
//   the failure the whole consent surface exists to prevent. It is drawn in the
//   warning style, never the success one, and the server's sentence saying it is
//   worth doubting is under it. A green 97% would be this file agreeing with the
//   client's most flattering reading and disagreeing with the report.
//
//   A WITHHELD PERCENTAGE IS NOT A MISSING ONE. Below twenty people asked, the
//   server sends `rate: null` and keeps the counts, because at three people one
//   person is thirty-three points. The cell says "too few to say" rather than
//   "not reported", because the server is not short of the answer, it is
//   declining to reduce two honest numbers to a misleading one.
//
//   A PURPOSE WITH NO CONSENT EVENT IS NOT NOUGHT PER CENT. There is no event
//   for a refusal — a refusal is the absence of a consent — so a purpose nobody
//   was asked about and a purpose everybody refused look identical from the
//   spine, and those are opposite facts. Those purposes are named in their own
//   panel with both readings, and they are not rows in the table with a nought
//   in them.
//
// THE DENOMINATOR IS ON THE SCREEN IN WORDS, and it is the part a client will
// argue with. Every rate is over registrations *started*, not completed, because
// somebody who read the consent paragraph and walked away was asked and did not
// agree — and counting only completions would drop them from the bottom of the
// fraction as well as the top, raising the rate for the worst reason there is.
// Completions are drawn beside it and nothing divides by them: the gap between
// the two is exactly the population the flattering denominator would hide, and a
// reader who can see both cannot be handed the wrong one without noticing.
//
// AND THE FOUR THINGS IT CANNOT TELL YOU ARE DRAWN, not folded away. A rate
// whose caveats are only in the source is a rate that gets quoted without them,
// and the first of them — that the consent controls can be below the fold on a
// tablet, so "asked" means the question was on the screen somebody was given —
// is the difference between a wording problem and a layout problem.

import { useEffect, useState, type ReactNode } from "react";
import { NoticeBox, PageHead, Tag } from "@nova/desk-ui/parts";
import { SecLabel } from "@nova/desk-ui/kit";
import { Empty, Loading } from "@nova/desk-ui/states";
import type { OptInRates, PurposeOptIn } from "../../../operator/src/optIn.js";
import { readReport, type ApiOptions, type Failure } from "../api/client.js";
import { count, moment, share, TOO_FEW, UNKNOWN } from "../operator/present.js";
import { ReadFailed } from "./ReadFailed.js";
import { useShow } from "./Show.js";
import type { SessionToken } from "../session/store.js";

export type OptInProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

type State =
  | { readonly phase: "reading" }
  | { readonly phase: "read"; readonly rates: OptInRates }
  | { readonly phase: "failed"; readonly failure: Failure };

export function OptIn({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
}: OptInProps): ReactNode {
  const show = useShow();
  const [state, setState] = useState<State>({ phase: "reading" });
  // Bumped by "Ask again", which is the whole of what a retry needs: the
  // effect below reads again whenever it changes.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (show.status !== "ready") return;
    let cancelled = false;
    setState({ phase: "reading" });

    void (async () => {
      const result = await readReport<OptInRates>(api, token, "optIn", activationId);
      if (cancelled) return;

      if (result.ok) {
        setState({ phase: "read", rates: result.value });
        return;
      }

      setState({ phase: "failed", failure: result });
      if (result.kind === "session_ended") onSessionEnded(result.detail);
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, show.status, attempt]);

  // The head is on the page whatever the read did, as the design draws it:
  // a reader whose report failed is still on the Consent page of this show.
  const head = (lede?: string, note?: string) => (
    <PageHead
      title="Consent"
      lede={lede ?? "What people agreed to, over the registrations started rather than the ones that finished."}
      uuid={activationId}
      {...(note === undefined ? {} : { note })}
      explain="info"
    />
  );
  // "Ask again" asks again for whatever failed: the show's own read, which the
  // report waits on, or the report itself.
  const retry = () => (show.status === "failed" ? show.reload() : setAttempt((n) => n + 1));
  const failure = show.status === "failed" ? show.failure : state.phase === "failed" ? state.failure : null;
  if (failure !== null) {
    return (
      <div className="stack">
        {head()}
        <ReadFailed
          what="Could not load the consent report"
          failure={failure}
          role={role}
          onEnrol={onEnrol}
          notCounted="No consent figures are shown because the report did not load."
          onRetry={retry}
        />
      </div>
    );
  }
  if (show.status !== "ready" || state.phase !== "read") {
    return (
      <div className="stack">
        {head()}
        <Loading rows={4} cols={6} label="Reading consent…" />
      </div>
    );
  }

  const rates = state.rates;
  const purposes = rates.purposes ?? [];
  const unobserved = rates.unobserved?.purposes ?? [];
  return (
    <div className="stack">
      {head(rates.denominator?.asked_means)}

      <section>
        <SecLabel>Who was asked</SecLabel>
        {/* Asked, and the sign each lane leaves that makes it so: a start on a
            tablet, a passcode proved on the website, a finish on a page that
            records no start (TASKS §143; the website used to count as nobody). */}
        <div className="figs">
          <Figure label="Asked" value={count(rates.denominator?.asked)} />
          <Figure label="Started on a tablet" value={count(rates.denominator?.registrations_started)} />
          <Figure label="Proved on the website" value={count(rates.denominator?.mailboxes_proved)} />
          <Figure label="Finished with no start" value={count(rates.denominator?.completed_without_start)} />
          {/* Here so the flattering denominator is visible beside the honest
              one. Nothing on this screen divides by it, and the label says so
              rather than leaving two numbers of equal standing side by side. */}
          <Figure label="Registrations completed" value={count(rates.denominator?.registrations_completed)} />
        </div>
      </section>

      <section>
        <SecLabel info={byPurposeInfo(purposes)}>By purpose</SecLabel>
        <Purposes purposes={purposes} />
      </section>

      {unobserved.length === 0 ? null : (
        <section>
          <SecLabel>{`Nothing recorded for ${unobserved.join(", ")}`}</SecLabel>
          <NoticeBox kind="amber" title={`No consent activity recorded for ${unobserved.join(", ")}`}>
            {/* The server's own sentence, which states both readings. This panel
                is the alternative to a row of noughts, and the reason it exists is
                that the two readings are opposite facts. */}
            <p>{rates.unobserved?.note ?? ""}</p>
          </NoticeBox>
        </section>
      )}

      <section>
        <SecLabel>Limits of this report</SecLabel>
        <div className="panel panel-stack">
          {(rates.denominator?.cannot_tell_you ?? []).map((caveat, index) => (
            <p key={index}>{caveat}</p>
          ))}
          {rates.scope_note ? <p className="help help-tight">{rates.scope_note}</p> : null}
          {/* When it was read and under whose access, with the report's other
              limits rather than in the head, which the design keeps to the
              page's name and what its rates are over. */}
          <p className="help help-tight">
            Read at {moment(rates.as_at)}. {rates.access_note ?? "Access details are unavailable."}
          </p>
        </div>
      </section>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="fig">
      <span className="fig-l">{label}</span>
      <span className={value === UNKNOWN ? "fig-v num unk" : "fig-v num"}>{value}</span>
    </div>
  );
}

function Purposes({ purposes }: { purposes: PurposeOptIn[] }): ReactNode {
  if (purposes.length === 0) {
    return (
      <Empty title="Nobody has been asked anything">
        <p>
          No consent activity has been recorded. No percentage is shown because missing data is not the
          same as 0%.
        </p>
      </Empty>
    );
  }
  return (
    <div className="tablewrap">
      <div className="tablescroll">
        <table>
          <thead>
            <tr>
              {/* Plain headings over the counts, as the design draws this
                  table: .num is the cell's figure face, not the heading's. */}
              <th>Purpose</th>
              <th>Asked</th>
              <th>Agreed</th>
              <th>Took it back</th>
              <th>Rate</th>
              <th>Reading</th>
            </tr>
          </thead>
          <tbody>
            {purposes.map((purpose) => (
              <tr key={purpose.purpose}>
                <td data-label="Purpose">
                  <span className="mono">{purpose.purpose}</span>
                  {/* The server's sentence, which is where the reading is
                      explained: at a high rate it says the figure is worth
                      doubting and why, and at a low one it says to check the
                      control is reachable before concluding the wording is being
                      refused. Both are instructions this console must not
                      paraphrase into a colour. */}
                  <span className="cell-sub">{purpose.says}</span>
                </td>
                <td data-label="Asked" className="num">{count(purpose.asked)}</td>
                <td data-label="Agreed" className="num">{count(purpose.granted)}</td>
                <td data-label="Took it back" className="num">{count(purpose.withdrawn)}</td>
                <td data-label="Rate" className="num">
                  {share(purpose.rate) === TOO_FEW ? <span className="unk">{share(purpose.rate)}</span> : share(purpose.rate)}
                  {purpose.one_person_moves_it_by === null ? null : (
                    <span className="cell-sub">±{purpose.one_person_moves_it_by} per person</span>
                  )}
                </td>
                <td data-label="Reading">
                  <Reading reading={purpose.reading} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * What the By purpose heading's (i) says: why the two readings that look like
 * answers are not, said about the rows this show actually has.
 *
 * The October hand-off writes it with its sample's numbers (97%, three
 * people). Those are a fixture's, and a sentence about a rate the table under
 * it does not show is worse than none, so this names this show's own: the
 * first purpose read as worth doubting, by its rate, and the first one with
 * too few people to rate, by its count and what one of them is worth. A show
 * with neither has no (i), because there is nothing on the table to warn
 * about. The per-row sentence the server writes stays in each row; this is
 * the heading's reason, not a paraphrase of those.
 */
function byPurposeInfo(purposes: PurposeOptIn[]): string | undefined {
  const said: string[] = [];
  const high = purposes.find((purpose) => purpose.reading === "nearly_everyone");
  if (high !== undefined) {
    said.push(
      `${share(high.rate)} is worth doubting rather than celebrating: a rate that high is the signature of people ticking whatever is in front of them to reach the button.`,
    );
  }
  const few = purposes.find((purpose) => purpose.reading === "too_few" && purpose.asked > 0);
  if (few !== undefined) {
    const who = few.asked === 1 ? "person" : "people";
    said.push(
      `${count(few.asked)} ${who} is not a percentage — at ${count(few.asked)}, one person is ${Math.round(100 / few.asked)} points.`,
    );
  }
  return said.length === 0 ? undefined : said.join(" ");
}

/** The reading as the design's tag. A rate near everybody is not a success. */
function Reading({ reading }: { reading: string }): ReactNode {
  if (reading === "unremarkable") return <span className="tag tag-good">Normal</span>;
  if (reading === "nearly_everyone") return <Tag>Worth doubting</Tag>;
  if (reading === "almost_nobody") return <Tag>Almost nobody</Tag>;
  if (reading === "too_few") return <Tag off>Too few to say</Tag>;
  return <Tag off>{reading}</Tag>;
}
